/**
 * Rogue Alloy accessories, upsell and sale-help system (package acc-theme-
 * upsell, 26 September 2026 - see design/accessories-and-upsell-2026-09-26.md
 * sections 4, 5 and 8).
 *
 * Replaces snippets/rogue-accessory-cross-sell.liquid (emptied - it
 * preferred locking nuts, TPMS and spigot rings, all three already inside
 * "What comes with your set"). Everything here works from the same
 * exclusion list so a set is never sold its own fixings twice.
 *
 * Every function down to the "Custom elements" heading is pure - no DOM, no
 * network, no storage - and is unit tested by tests/rogue-upsell.test.js
 * (`node --test`, run from a plain Node process with no `document` or
 * `window`). The custom element classes below that heading use `document`,
 * `window`, `fetch` and storage, so the whole block is guarded behind a
 * `typeof HTMLElement !== 'undefined'` check: without it, `class X extends
 * HTMLElement` throws at module-evaluation time the moment Node imports this
 * file for the pure-function tests, because `HTMLElement` does not exist
 * there. The pure functions above the guard are declared, not executed, at
 * import time, so they always import cleanly regardless.
 *
 * No third-party libraries. No bundler: loaded directly as an ES module,
 * the same convention assets/rogue-fitment.js uses.
 */

import { addBasketItemsOnce, beginCartMutation, packageSpareQuantity, wheelQuantityValue } from '#rogue/fitment';

// One intent per variant across the in-page panel, overlay and cart. Rendering
// a stale cart response must not unlock a request that is still in flight.
const upsellAttemptsKey = Symbol.for('rogue.upsellAttempts');
const upsellAttempts = typeof window === 'undefined' ? new Map() : (window[upsellAttemptsKey] ||= new Map());

function reflectUpsellAttempt(button, attempt) {
  if (!attempt) return;
  button.disabled = attempt.state === 'pending';
  button.hidden = attempt.state === 'accepted' && attempt.refreshed;
  button.dataset.checkBasket = String(attempt.state !== 'pending');
  button.textContent = attempt.state === 'pending' ? 'Adding…' : attempt.state === 'accepted' ? 'Added — view basket' : 'Check basket';
  const added = button.closest('[data-rogue-upsell-row]')?.querySelector('.rogue-upsell-row__added');
  if (added) added.hidden = !button.hidden;
}

/* ===========================================================================
 * Pure functions
 * ======================================================================= */

/** Legacy exported default; current TPMS demand uses each explicit package quantity. */
export const DEFAULT_WHEEL_SET_SIZE = 4;

/** Fixed row order (design spec 4.4): TPMS, brace, Rinse, cover. */
export const UPSELL_ROW_ORDER = ['tpms', 'brace', 'rinse', 'cover'];

/**
 * Total quantity of Wheel-type lines in the basket.
 * @param {Array<Record<string, unknown>>} cartItems - `/cart.js`'s `items` array
 * @returns {number}
 */
export function wheelQuantity(cartItems) {
  if (!Array.isArray(cartItems)) return 0;
  return cartItems.reduce((total, item) => {
    if (!item || item.product_type !== 'Wheel') return total;
    const qty = Number(item.quantity);
    return total + (Number.isFinite(qty) ? qty : 0);
  }, 0);
}

/** Whether a wheel package explicitly records a spare. Quantity alone is never evidence. */
export function hasSpare(cartItems) {
  if (!Array.isArray(cartItems)) return false;
  return cartItems.some((item) => item?.product_type === 'Wheel' && packageSpareQuantity(item) > 0);
}

/** Each package keeps its own van. Unknown legacy packages need a check, not the last selected van. */
export function tpmsRequirement(cartItems, tpmsVans, candidate, previewPackage = null) {
  const wheels = (cartItems || []).filter((item) => item?.product_type === 'Wheel');
  const packages = wheels.length ? wheels : previewPackage ? [previewPackage] : [];
  let required = 0;
  let unknown = false;
  for (const item of packages) {
    const slug = item.properties?._vehicle_slug;
    if (!slug || slug === 'unlisted') { unknown = true; continue; }
    if (tpmsVans.includes(slug)) required += (wheelQuantityValue(item.quantity) || 0) - packageSpareQuantity(item);
  }
  const existing = (cartItems || []).filter((item) => candidate?.handle && item?.handle === candidate.handle)
    .reduce((sum, item) => sum + (wheelQuantityValue(item.quantity) || 0), 0);
  return { required, existing, missing: Math.max(0, required - existing), excess: Math.max(0, existing - required), unknown };
}

/** @param {Array<Record<string, unknown>>} cartItems @returns {boolean} whether any Tyre-type line is in the basket */
function hasTyreLine(cartItems) {
  return Array.isArray(cartItems) && cartItems.some((item) => item && item.product_type === 'Tyre');
}

/** @param {Array<Record<string, unknown>>} cartItems @param {string} handle @returns {boolean} */
function isAlreadyInBasket(cartItems, handle) {
  if (!handle || !Array.isArray(cartItems)) return false;
  return cartItems.some((item) => item && item.handle === handle);
}

/**
 * @param {unknown} tags
 * @param {unknown} excludeTags
 * @returns {boolean} whether `tags` contains any tag from `excludeTags` (case-insensitive)
 */
function hasAnyExcludedTag(tags, excludeTags) {
  if (!Array.isArray(tags) || tags.length === 0) return false;
  if (!Array.isArray(excludeTags) || excludeTags.length === 0) return false;
  const excludeSet = new Set(
    excludeTags.map((tag) => String(tag).trim().toLowerCase()).filter((tag) => tag !== '')
  );
  return tags.some((tag) => excludeSet.has(String(tag).trim().toLowerCase()));
}

/** The storefront's active currency (a European market's local currency, D65), or GBP. */
function storefrontCurrency() {
  const code = typeof window !== 'undefined' ? window.Shopify?.currency?.active : undefined;
  return typeof code === 'string' && /^[A-Z]{3}$/.test(code) ? code : 'GBP';
}

/**
 * Formats a pence amount, matching `assets/rogue-fitment.js`'s
 * `formatGbpPence` exactly ("£1,660" for a round number, "£219.99"
 * otherwise, and the local currency's symbol once a European market is
 * active) - kept as its own copy here rather than an import, so this
 * package never depends on a file another workflow owns.
 * @param {number} pence
 * @param {string} [currency] ISO 4217; defaults to the storefront's active currency, else GBP
 * @returns {string} "" when `pence` is not a usable non-negative number
 */
export function formatPence(pence, currency = storefrontCurrency()) {
  if (typeof pence !== 'number' || !Number.isFinite(pence) || pence < 0) return '';
  const whole = Math.round(pence);
  const pounds = Math.floor(whole / 100);
  const remainder = whole % 100;
  if (currency !== 'GBP') {
    const digits = remainder === 0 ? 0 : 2;
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(whole / 100);
    } catch {
      // An unknown code falls through to the plain GBP shape below rather than throwing.
    }
  }
  if (remainder === 0) return `£${pounds.toLocaleString('en-GB')}`;
  return `£${pounds.toLocaleString('en-GB')}.${String(remainder).padStart(2, '0')}`;
}

/**
 * "4 x £29.99, £119.96" (design spec 4.1 rule 2: every price shown with its
 * quantity and total).
 * @param {number} quantity
 * @param {number|null|undefined} unitPricePence
 * @returns {string} "" when there is nothing usable to price
 */
export function priceLabel(quantity, unitPricePence) {
  const qty = Number(quantity) > 0 ? Math.round(Number(quantity)) : 0;
  const unit = typeof unitPricePence === 'number' && Number.isFinite(unitPricePence) ? unitPricePence : null;
  if (qty === 0 || unit === null) return '';
  // 25 Oct 2026: one item is just its price ("1 x £9.99, £9.99" read oddly).
  if (qty === 1) return formatPence(unit);
  return `${qty} x ${formatPence(unit)}, ${formatPence(unit * qty)}`;
}

/** A basket line's price after discounts, in pence (0 when unreadable). */
function linePence(item) {
  const value = typeof item?.final_line_price === 'number' ? item.final_line_price : item?.line_price;
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** "Satin Black, 16 inch" from a wheel line's Colour and Size options. */
function wheelOptionLabel(item) {
  const named = Array.isArray(item?.options_with_values) ? item.options_with_values : [];
  const pick = (names) => named.find((option) => names.includes(String(option?.name || '').toLowerCase()))?.value || '';
  const parts = named.length
    ? [pick(['colour', 'color']), pick(['size'])]
    : String(item?.variant_title || '').split(' / ').slice(0, 2);
  return parts.map((part) => String(part || '').trim()).filter(Boolean).join(', ');
}

/**
 * The set that was just added, as one line priced from the basket itself:
 * the wheels, their fitted tyres and anything else added with them (every
 * line sharing the wheel line's `_set_id`). 25 Oct 2026: the sheet read
 * "4 x CC1 ... £780" after £1,480 went in with tyres.
 * @param {Array<Record<string, any>>} cartItems - `/cart.js` items (newest first)
 * @param {{ variantIds?: Array<string|number>, setId?: string }} [added] - this product's variant ids, and the package id the buy box just used
 * @returns {{ text: string, totalPence: number, lineCount: number }|null}
 */
/**
 * 25 Oct 2026 (Daniel): whether the set just added is one whose fit Rogue
 * double-checks after the order (a manual-review vehicle, or "not sure" of
 * the stud pattern) - read from the same hidden marker the platform holds
 * on, never changed here.
 * @param {Array<Record<string, any>>} cartItems
 * @param {{ setId?: string }} [options]
 * @returns {boolean}
 */
export function addedSetNeedsFitCheck(cartItems, { setId = '' } = {}) {
  const wheels = (Array.isArray(cartItems) ? cartItems : []).filter((item) => item && item.product_type === 'Wheel');
  const inSet = setId ? wheels.filter((item) => item.properties?._set_id === setId) : wheels;
  return inSet.some((item) => item.properties?._fitment_verification === 'requested' || item.properties?.fitment_verification === 'requested');
}

/**
 * The line the post-add sheet opens with: the wheel line just added, its
 * fitted tyres (same `_set_id`) and, since 31 Oct 2026 (T2), the extras
 * ticked in the buy box and added in the same click (`extraVariantIds`,
 * from the add event; accessories never carry a `_set_id`).
 */
export function addedSetSummary(cartItems, { variantIds = [], setId = '', extraVariantIds = [] } = {}) {
  const items = Array.isArray(cartItems) ? cartItems.filter(Boolean) : [];
  const ids = (Array.isArray(variantIds) ? variantIds : []).map(String);
  const extraIds = (Array.isArray(extraVariantIds) ? extraVariantIds : []).map(String);
  const isWheel = (item) => item.product_type === 'Wheel';
  const wheel = (setId && items.find((item) => isWheel(item) && item.properties?._set_id === setId))
    || items.find((item) => isWheel(item) && ids.includes(String(item.variant_id)));
  if (!wheel) return null;
  const packageId = typeof wheel.properties?._set_id === 'string' ? wheel.properties._set_id : '';
  const inSet = packageId ? items.filter((item) => item !== wheel && item.properties?._set_id === packageId) : [];
  const extras = items.filter((item) => item !== wheel && !inSet.includes(item) && !isWheel(item) && !item.properties?._set_id && extraIds.includes(String(item.variant_id)));
  const others = [...inSet, ...extras];
  const describe = (item) => {
    const qty = Number(item.quantity) || 0;
    const title = String(item.product_title || item.title || '').trim();
    if (item === wheel) {
      const options = wheelOptionLabel(item);
      return `${qty} × ${title}${options ? ` (${options})` : ''}`;
    }
    return `${qty} × ${title}${item.product_type === 'Tyre' ? ' fitted' : ''}`;
  };
  const lines = [wheel, ...others];
  const totalPence = lines.reduce((sum, item) => sum + linePence(item), 0);
  return { text: `${lines.map(describe).join(' + ')}: ${formatPence(totalPence)}`, totalPence, lineCount: lines.length };
}

/**
 * Whether the sheet may offer "Check out" straight away. A tyre line whose
 * wheel line is gone (no wheel shares its `_set_id`) must be dealt with in
 * the basket first - the same rule the basket's own checkout buttons follow
 * (theme README, "Tyres are only ever sold fitted").
 * @param {Array<Record<string, any>>} cartItems
 * @returns {boolean}
 */
export function basketReadyForCheckout(cartItems) {
  const items = Array.isArray(cartItems) ? cartItems.filter(Boolean) : [];
  if (items.length === 0) return false;
  const wheelSets = new Set(items.filter((item) => item.product_type === 'Wheel').map((item) => item.properties?._set_id).filter(Boolean));
  return items.every((item) => item.product_type !== 'Tyre' || wheelSets.has(item.properties?._set_id));
}

/**
 * The post-add sheet's two top buttons while a Whack Friday drop is live
 * (3 Nov 2026, Black Friday review F3). Every drop order gets a free hoodie
 * or T-shirt, chosen in the basket note, and checkout has no note: during a
 * drop "Go to basket" is the main button, first, and the merch line shows.
 * Outside a drop the sheet is as it always was: "Check out" first and main.
 * @param {{ actions?: Element|null, checkout?: Element|null, goto?: Element|null, merch?: Element|null }} parts
 * @param {boolean} live - <html data-rogue-drop="live"> (snippets/rogue-freeship-state.liquid, assets/rogue-freeship.js)
 */
export function applyDropSheet({ actions, checkout, goto, merch }, live) {
  if (checkout && goto) {
    checkout.classList.toggle('rogue-btn--primary', !live);
    checkout.classList.toggle('rogue-btn--ghost', live);
    goto.classList.toggle('rogue-btn--primary', live);
    goto.classList.toggle('rogue-btn--ghost', !live);
    const first = live ? goto : checkout;
    if (actions && actions.firstElementChild !== first) actions.insertBefore(first, actions.firstElementChild);
  }
  if (merch) merch.hidden = !live;
}

/**
 * The line without any sentence naming `code`: the post-add sheet's total
 * line while a Whack Friday drop is live (1 Nov 2026), so it drops "Free UK
 * delivery with code FREESHIP." and keeps the total.
 * @param {string} text
 * @param {string} code
 * @returns {string}
 */
export function withoutCodeSentences(text, code) {
  const needle = String(code || '').toUpperCase();
  if (!needle) return String(text);
  return String(text)
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => !sentence.toUpperCase().includes(needle))
    .join(' ');
}

/**
 * "Only 6 left in this size and finish." (design spec 5.2). "" for anything
 * that isn't a usable positive count, so the caller removes the note rather
 * than printing a broken sentence.
 * @param {number} qty
 * @returns {string}
 */
export function lowStockText(qty) {
  const n = Number(qty);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `Only ${Math.round(n)} left in this size and finish.`;
}

/* ---------------------------------------------------------------------------
 * Postcode delivery check (design spec 5.1)
 * ------------------------------------------------------------------------ */

// Outward code shapes: A9, A99, A9A, AA9, AA99, AA9A - one letter-class
// covers all six because the optional trailing character can be a letter or
// a digit either way.
const OUTWARD_RE = /^[A-Z]{1,2}\d[A-Z0-9]?$/;
// Inward code: always one digit then two letters ("9AA").
const INWARD_RE = /^\d[A-Z]{2}$/;

/**
 * Upper-cases and strips spaces, then accepts either a full UK postcode or
 * an outward code alone. Only the outward code is ever kept - never the
 * inward part, which would identify an address too precisely to store.
 * @param {string} input
 * @returns {{ valid: boolean, outward: string, area: string }}
 */
export function parsePostcode(input) {
  const cleaned = typeof input === 'string' ? input.toUpperCase().replace(/\s+/g, '') : '';
  if (cleaned.length >= 5) {
    const inward = cleaned.slice(-3);
    const outward = cleaned.slice(0, -3);
    if (INWARD_RE.test(inward) && OUTWARD_RE.test(outward)) {
      return { valid: true, outward, area: areaFromOutward(outward) };
    }
  }
  if (OUTWARD_RE.test(cleaned)) {
    return { valid: true, outward: cleaned, area: areaFromOutward(cleaned) };
  }
  return { valid: false, outward: '', area: '' };
}

/** @param {string} outward @returns {string} the leading letters ("RG30" -> "RG") */
function areaFromOutward(outward) {
  const match = /^[A-Z]{1,2}/.exec(outward);
  return match ? match[0] : '';
}

/**
 * @typedef {{ type: 'prefix', value: string } | { type: 'range', prefix: string, from: number, to: number }} PostcodeToken
 */

/**
 * Parses a zone-list setting ("AB, IV, KW" or "AB31-AB38, TR21-TR25") into
 * matchable tokens. Comma or newline separated, so it works as either a
 * `text` or `textarea` setting.
 * @param {string} text
 * @returns {PostcodeToken[]}
 */
export function parsePrefixList(text) {
  if (typeof text !== 'string') return [];
  return text
    .split(/[,\n]/)
    .map((token) => token.trim().toUpperCase())
    .filter((token) => token !== '')
    .map((token) => {
      const rangeMatch = /^([A-Z]{1,2})(\d+)-([A-Z]{1,2})?(\d+)$/.exec(token);
      if (rangeMatch) {
        return { type: 'range', prefix: rangeMatch[1], from: Number(rangeMatch[2]), to: Number(rangeMatch[4]) };
      }
      return { type: 'prefix', value: token };
    });
}

/**
 * Splits a comma- or newline-separated settings string into trimmed,
 * non-empty tokens - used for the TPMS van list, the spare-cover body
 * types and the exclude-tag list (design spec 3.6/4.1), each a plain list
 * with no postcode-range syntax, unlike {@link parsePrefixList}.
 * @param {string} text
 * @returns {string[]}
 */
export function parseSimpleList(text) {
  if (typeof text !== 'string') return [];
  return text
    .split(/[,\n]/)
    .map((token) => token.trim())
    .filter((token) => token !== '');
}

/** @param {string} outward @param {PostcodeToken} token @returns {boolean} */
function matchesToken(outward, token) {
  if (!token) return false;
  if (token.type === 'prefix') return outward.startsWith(token.value);
  if (token.type === 'range') {
    if (!outward.startsWith(token.prefix)) return false;
    const rest = outward.slice(token.prefix.length);
    const num = parseInt(rest, 10);
    return Number.isFinite(num) && num >= token.from && num <= token.to;
  }
  return false;
}

const ZONE_CHECK_ORDER = ['channel', 'ni', 'iom', 'highlands', 'islands'];

/**
 * @param {string} outward - a valid outward code, from {@link parsePostcode}
 * @param {{ channel?: PostcodeToken[], ni?: PostcodeToken[], iom?: PostcodeToken[], highlands?: PostcodeToken[], islands?: PostcodeToken[] }} lists - each already parsed by {@link parsePrefixList}
 * @returns {'channel'|'ni'|'iom'|'highlands'|'islands'|'mainland'|'invalid'}
 */
export function postcodeZone(outward, lists) {
  if (typeof outward !== 'string' || outward === '') return 'invalid';
  const safeLists = lists && typeof lists === 'object' ? lists : {};
  for (const zone of ZONE_CHECK_ORDER) {
    const tokens = Array.isArray(safeLists[zone]) ? safeLists[zone] : [];
    if (tokens.some((token) => matchesToken(outward, token))) return zone;
  }
  return 'mainland';
}

/* ---------------------------------------------------------------------------
 * Upsell candidates (design spec 4.4)
 * ------------------------------------------------------------------------ */

/**
 * @typedef {object} UpsellCandidate
 * @property {string} handle
 * @property {boolean} available
 * @property {string[]} tags
 * @property {number|null} [price] - unit price in pence
 * @property {string|null} [image]
 * @property {number|string|null} [variantId]
 * @property {string} [title]
 * @property {string} [reason]
 * @property {string} [tyreFittedNote] - appended to the TPMS reason once a tyre line is in the basket
 */

/**
 * @typedef {object} UpsellRow
 * @property {'tpms'|'brace'|'rinse'|'cover'} kind
 * @property {string} handle
 * @property {string} title
 * @property {string} reason
 * @property {number} quantity
 * @property {number|null} unitPrice
 * @property {string} priceLabel
 * @property {string|null} image
 * @property {number|string|null} variantId
 * @property {boolean} outOfStock
 */

/**
 * The core upsell decision, shared by the wheel product page's in-page block
 * and post-add panel, and by the basket's "Finish your set" rows. Fixed
 * order (TPMS, brace, Rinse, cover); every row is available, untagged with
 * any exclude tag, and not already in the basket (design spec 4.1 rules 4
 * and 6).
 *
 * @param {object} params
 * @param {Array<Record<string, unknown>>} [params.cartItems] - `/cart.js`'s `items`
 * @param {string} [params.vanSlug] - the stored vehicle's slug, or ""
 * @param {string} [params.vanBodyType] - the stored vehicle's body_type, or ""
 * @param {string[]} [params.tpmsVans] - van slugs that show a dashboard tyre-pressure reading
 * @param {{ tpms?: UpsellCandidate, brace?: UpsellCandidate, rinse?: UpsellCandidate, cover?: UpsellCandidate }} [params.candidates]
 * @param {string[]} [params.excludeTags]
 * @param {boolean} [params.showCover]
 * @param {string[]} [params.coverBodyTypes]
 * @param {boolean} [params.previewMode] - `?rogue_upsell_preview=1`: sold-out candidates still render, flagged `outOfStock`
 * @returns {{ rows: UpsellRow[], showTpmsLink: boolean }}
 */
export function upsellRows({
  cartItems = [],
  vanSlug = '',
  vanBodyType = '',
  tpmsVans = [],
  candidates = {},
  excludeTags = [],
  showCover = false,
  coverBodyTypes = [],
  previewMode = false,
  previewPackage = null,
} = {}) {
  const safeCartItems = Array.isArray(cartItems) ? cartItems : [];
  const safeTpmsVans = Array.isArray(tpmsVans) ? tpmsVans : [];
  const safeCoverBodyTypes = Array.isArray(coverBodyTypes) ? coverBodyTypes : [];
  const safeCandidates = candidates && typeof candidates === 'object' ? candidates : {};

  const vanInTpmsList = typeof vanSlug === 'string' && vanSlug !== '' && safeTpmsVans.includes(vanSlug);
  const rows = [];
  const tpms = tpmsRequirement(safeCartItems, safeTpmsVans, safeCandidates.tpms, previewPackage);

  for (const kind of UPSELL_ROW_ORDER) {
    const candidate = safeCandidates[kind];
    if (!candidate || typeof candidate !== 'object') continue;

    if (kind === 'tpms' && tpms.missing === 0) continue;

    if (kind === 'cover') {
      if (!showCover) continue;
      if (!hasSpare(safeCartItems)) continue;
      if (!vanBodyType || !safeCoverBodyTypes.includes(vanBodyType)) continue;
    }

    if (hasAnyExcludedTag(candidate.tags, excludeTags)) continue;
    if (kind !== 'tpms' && isAlreadyInBasket(safeCartItems, candidate.handle)) continue;

    const outOfStock = candidate.available === false;
    if (outOfStock && !previewMode) continue;

    let quantity = 1;
    if (kind === 'tpms') {
      quantity = tpms.missing;
    }

    let reason = typeof candidate.reason === 'string' ? candidate.reason : '';
    if (kind === 'tpms') reason = `${tpms.required} road-wheel ${tpms.required === 1 ? 'sensor' : 'sensors'} needed; ${tpms.existing} already in your basket. Add the remaining ${tpms.missing}.`;
    if (kind === 'tpms' && hasTyreLine(safeCartItems) && candidate.tyreFittedNote) {
      reason = `${reason} ${candidate.tyreFittedNote}`.trim();
    }

    const unitPrice = typeof candidate.price === 'number' && Number.isFinite(candidate.price) ? candidate.price : null;

    rows.push({
      kind,
      handle: candidate.handle || '',
      title: typeof candidate.title === 'string' ? candidate.title : '',
      reason,
      quantity,
      unitPrice,
      priceLabel: unitPrice === null ? '' : candidate.needsChoice ? `${candidate.priceVaries ? 'From ' : ''}${formatPence(unitPrice)}` : priceLabel(quantity, unitPrice),
      image: candidate.image || null,
      variantId: candidate.variantId ?? null,
      outOfStock,
    });
  }

  // Review finding S2 (26 Sep 2026): the fallback link showed for any van
  // not on the TPMS list even when the TPMS candidate itself was out of
  // stock, linking to a dead-end product page (seen live with a Ducato
  // stored). It only makes sense to offer once there is something to sell.
  const tpmsCandidate = safeCandidates.tpms;
  const tpmsCandidateAvailable = Boolean(tpmsCandidate) && tpmsCandidate.available !== false;
  const tpmsNote = tpms.unknown ? 'Not sure your van uses TPMS sensors? Pick your vehicle, or give us a call before adding them.'
    : tpms.excess ? `Your basket has ${tpms.existing} ${tpms.existing === 1 ? 'sensor' : 'sensors'} for ${tpms.required} eligible road ${tpms.required === 1 ? 'wheel' : 'wheels'}. Review the extra ${tpms.excess} in your basket.` : '';
  return { rows, showTpmsLink: (!vanInTpmsList || tpms.unknown) && tpmsCandidateAvailable, tpmsNote };
}

/**
 * The buy box's extras (31 Oct 2026, Skye: add-ons during the process, not
 * after it): the rows upsellRows picked for the basket as it would be after
 * this add, keeping only those that can simply be ticked - in stock, one
 * variant, a price and a quantity.
 * @param {UpsellRow[]} rows
 * @param {Record<string, UpsellCandidate>} [candidates]
 * @returns {UpsellRow[]}
 */
export function buyBoxExtraRows(rows, candidates = {}) {
  return (Array.isArray(rows) ? rows : []).filter((row) => row && !row.outOfStock && row.variantId != null && row.variantId !== ''
    && Number(row.quantity) > 0 && typeof row.unitPrice === 'number' && !candidates?.[row.kind]?.needsChoice);
}

/**
 * What the ticked extras add: the basket items, their total in pence and the
 * words for the buy box's summary line ("4 x Tyre pressure sensors and
 * Universal wheel brace").
 * @param {UpsellRow[]} rows - as buyBoxExtraRows returned them
 * @param {Iterable<string>} ticked - the ticked rows' kinds
 * @returns {{ items: Array<{ id: string|number, quantity: number }>, cents: number, summary: string }}
 */
/**
 * One buy-box extra as a compact row (B5, 31 Oct 2026): a label with the
 * tickbox, name and price, a "Why?" button, and the reason hidden until it
 * is pressed. Pure DOM building, so the shape can be tested in Node with a
 * stand-in `document`.
 * @param {UpsellRow} row
 * @param {string} reason
 * @param {boolean} ticked
 * @param {Set<string>} [openReasons] - kinds whose reason is open already
 * @param {Document} [doc]
 * @returns {HTMLElement}
 */
export function buildBuyBoxExtraRow(row, reason, ticked, openReasons = new Set(), doc = document) {
  const wrap = doc.createElement('div');
  wrap.className = 'rogue-buy__extra';
  const label = doc.createElement('label');
  label.className = 'rogue-buy__extra-main';
  const box = doc.createElement('input');
  box.type = 'checkbox';
  box.dataset.kind = row.kind;
  box.checked = Boolean(ticked);
  const title = doc.createElement('span');
  title.className = 'rogue-buy__extra-title';
  title.textContent = row.title;
  const price = doc.createElement('span');
  price.className = 'rogue-buy__extra-price';
  price.textContent = row.priceLabel;
  label.append(box, title, price);
  wrap.append(label);
  const text = String(reason || '').trim();
  if (text) {
    const open = openReasons.has(row.kind);
    const reasonId = `RogueExtraWhy-${String(row.kind).replace(/[^a-z0-9_-]/gi, '')}`;
    const why = doc.createElement('button');
    why.type = 'button';
    why.className = 'rogue-buy__extra-why';
    why.dataset.kind = row.kind;
    why.setAttribute('aria-expanded', open ? 'true' : 'false');
    why.setAttribute('aria-controls', reasonId);
    why.setAttribute('aria-label', `Why ${row.title}?`);
    why.textContent = 'Why?';
    const reasonEl = doc.createElement('p');
    reasonEl.className = 'rogue-buy__extra-reason';
    reasonEl.id = reasonId;
    reasonEl.textContent = text;
    reasonEl.hidden = !open;
    wrap.append(why, reasonEl);
  }
  return wrap;
}

/**
 * Opens or closes the reason a "Why?" button controls.
 * @param {HTMLElement} why
 * @returns {boolean} whether the reason is now open
 */
export function toggleExtraReason(why) {
  const open = why.getAttribute('aria-expanded') !== 'true';
  why.setAttribute('aria-expanded', open ? 'true' : 'false');
  const reason = why.parentElement?.querySelector?.('.rogue-buy__extra-reason') || null;
  if (reason) reason.hidden = !open;
  return open;
}

export function extrasSelection(rows, ticked) {
  const kinds = new Set(ticked || []);
  const chosen = (Array.isArray(rows) ? rows : []).filter((row) => kinds.has(row.kind));
  const names = chosen.map((row) => (row.quantity > 1 ? `${row.quantity} x ${row.title}` : row.title));
  return {
    // D1 (31 Oct 2026): the sensor line says why its count differs from the wheels (no sensor for the spare).
    // Not a count: with sensors already in the basket the line adds only the missing ones.
    items: chosen.map((row) => (row.kind === 'tpms'
      ? { id: row.variantId, quantity: row.quantity, properties: { Note: 'One per road wheel' } }
      : { id: row.variantId, quantity: row.quantity })),
    cents: chosen.reduce((sum, row) => sum + row.unitPrice * row.quantity, 0),
    summary: names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] || '',
  };
}

/**
 * A simpler pick used by the basket's accessories-only ("You might also
 * need": Rinse, brace, sticker) and merch-only ("More from the workshop")
 * rows: fixed order, available, untagged with an exclude tag, not already
 * in the basket, up to `limit`. No van or spare logic - those candidates
 * never depend on either.
 * @param {object} params
 * @param {string[]} params.order - candidate keys, in the order to prefer them
 * @param {Record<string, UpsellCandidate>} params.candidates
 * @param {Array<Record<string, unknown>>} [params.cartItems]
 * @param {string[]} [params.excludeTags]
 * @param {number} [params.limit]
 * @returns {UpsellRow[]}
 */
export function pickAvailableCandidates({ order, candidates, cartItems = [], excludeTags = [], limit = Infinity }) {
  const safeCartItems = Array.isArray(cartItems) ? cartItems : [];
  const safeCandidates = candidates && typeof candidates === 'object' ? candidates : {};
  const rows = [];
  for (const key of Array.isArray(order) ? order : []) {
    if (rows.length >= limit) break;
    const candidate = safeCandidates[key];
    if (!candidate || typeof candidate !== 'object') continue;
    if (candidate.available === false) continue;
    if (hasAnyExcludedTag(candidate.tags, excludeTags)) continue;
    if (isAlreadyInBasket(safeCartItems, candidate.handle)) continue;
    const unitPrice = typeof candidate.price === 'number' && Number.isFinite(candidate.price) ? candidate.price : null;
    rows.push({
      kind: key,
      handle: candidate.handle || '',
      title: typeof candidate.title === 'string' ? candidate.title : '',
      reason: typeof candidate.reason === 'string' ? candidate.reason : '',
      quantity: 1,
      unitPrice,
      priceLabel: unitPrice === null ? '' : candidate.needsChoice ? `${candidate.priceVaries ? 'From ' : ''}${formatPence(unitPrice)}` : priceLabel(1, unitPrice),
      image: candidate.image || null,
      variantId: candidate.variantId ?? null,
      outOfStock: false,
    });
  }
  return rows;
}

/** @param {string|number|null|undefined} value @returns {string} the trailing digits of a GID ("gid://shopify/ProductVariant/123" -> "123"), or the value as-is */
function extractNumericId(value) {
  if (value === null || value === undefined) return '';
  const match = /(\d+)$/.exec(String(value));
  return match ? match[1] : String(value);
}

/* ===========================================================================
 * Browser-only helpers (not unit tested - each guards its own DOM/network
 * access so a missing element or a storage failure degrades quietly rather
 * than throwing).
 * ======================================================================= */

const VEHICLE_STORAGE_KEY = 'rogue.vehicle';
const VEHICLE_COOKIE_NAME = 'rogue_vehicle';

/** @returns {{ slug: string, display_name?: string }|null} the stored vehicle - localStorage first, then the cookie, same keys `assets/rogue-fitment.js` uses */
function readStoredVehicle() {
  try {
    const raw = window.localStorage.getItem(VEHICLE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.slug === 'string' && parsed.slug !== '') return parsed;
    }
  } catch {
    /* storage unavailable - fall through to the cookie */
  }
  try {
    const parts = document.cookie.split(';');
    for (const part of parts) {
      const eq = part.indexOf('=');
      if (eq === -1) continue;
      if (part.slice(0, eq).trim() !== VEHICLE_COOKIE_NAME) continue;
      const parsed = JSON.parse(decodeURIComponent(part.slice(eq + 1).trim()));
      if (parsed && typeof parsed.slug === 'string' && parsed.slug !== '') return parsed;
    }
  } catch {
    /* malformed cookie - no stored vehicle */
  }
  return null;
}

/** @param {string} slug @returns {string} the stored vehicle's `body_type`, read from the header's `[data-rogue-vehicles]` JSON, or "" */
function readVehicleBodyType(slug) {
  if (!slug) return '';
  try {
    const script = document.querySelector('[data-rogue-vehicles]');
    if (!script) return '';
    const vehicles = JSON.parse(script.textContent || '[]');
    if (!Array.isArray(vehicles)) return '';
    const match = vehicles.find((vehicle) => vehicle && vehicle.slug === slug);
    return match && typeof match.body_type === 'string' ? match.body_type : '';
  } catch {
    return '';
  }
}

/** @returns {Promise<any>} the parsed `/cart.js` response */
async function fetchCart() {
  // Only a request started after a confirmed TPMS addition can unlock a later
  // top-up. An older in-flight render must never unlock an uncertain/pending add.
  const confirmed = [...upsellAttempts].filter(([, value]) => value.kind === 'tpms' && value.state === 'accepted' && value.refreshed);
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Could not refresh basket');
  const cart = await response.json();
  if (!Array.isArray(cart?.items)) throw new Error('Could not read basket quantities');
  for (const [key, value] of confirmed) if (upsellAttempts.get(key) === value) upsellAttempts.delete(key);
  return cart;
}

/**
 * Dispatches the theme's own `@shopify/events` CartLinesUpdateEvent after an
 * upsell add, `detail.source: 'rogue-upsell'` so nothing here reopens its
 * own panel, and so the header cart bubble and cart drawer refresh - the
 * same pattern `assets/rogue-fitment.js`'s `announceCartUpdate` already uses.
 * @param {{ variantId: number|string, quantity: number, cartJson: any, context: 'product'|'cart' }} params
 */
async function dispatchUpsellAdd({ variantId, quantity, cartJson, context }) {
  try {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context,
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson) }),
        detail: { source: 'rogue-upsell' },
      })
    );
  } catch (error) {
    console.warn('[rogue-upsell] cart update event not dispatched:', error);
  }
}

/** @returns {boolean} */
function prefersReducedMotion() {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch {
    return false;
  }
}

/**
 * Fills one pre-rendered `.rogue-upsell-row` (snippets/rogue-upsell-row.liquid)
 * with a computed {@link UpsellRow} and reveals it; hides every row in
 * `container` that {@link rows} did not select. Rows are matched by
 * `data-kind`, scoped to `container` so the same kind can appear more than
 * once on a page (the in-page block and the post-add panel each carry their
 * own four rows).
 * @param {Element|null} container
 * @param {UpsellRow[]} rows
 * @param {number} max
 */
function renderUpsellRows(container, rows, max) {
  if (!container) return;
  const byKind = new Map(rows.slice(0, max).map((row) => [row.kind, row]));
  const rowEls = Array.from(container.querySelectorAll('[data-rogue-upsell-row]'));
  for (const el of rowEls) {
    const kind = el.getAttribute('data-kind');
    const row = kind ? byKind.get(kind) : undefined;
    if (!row) {
      el.hidden = true;
      continue;
    }
    fillUpsellRow(el, row);
    el.hidden = false;
  }
}

/** @param {Element} el @param {UpsellRow} row */
function fillUpsellRow(el, row) {
  const titleEl = el.querySelector('.rogue-upsell-row__title');
  const reasonEl = el.querySelector('.rogue-upsell-row__reason');
  const priceEl = /** @type {HTMLElement|null} */ (el.querySelector('.rogue-upsell-row__price'));
  const addedEl = /** @type {HTMLElement|null} */ (el.querySelector('.rogue-upsell-row__added'));
  const addBtn = /** @type {HTMLButtonElement|null} */ (el.querySelector('[data-rogue-upsell-add]'));
  const chooseLink = el.querySelector('[data-rogue-upsell-choose]');

  if (titleEl) titleEl.textContent = row.title;
  if (reasonEl) reasonEl.textContent = row.reason;
  if (priceEl) {
    priceEl.textContent = row.priceLabel;
    priceEl.hidden = row.priceLabel === '';
  }
  if (addedEl) addedEl.hidden = true;
  if (chooseLink) chooseLink.hidden = Boolean(row.outOfStock);
  if (addBtn) {
    addBtn.hidden = false;
    addBtn.disabled = Boolean(row.outOfStock);
    addBtn.dataset.variantId = row.variantId == null ? '' : String(row.variantId);
    addBtn.dataset.quantity = String(row.quantity || 1);
    addBtn.dataset.checkBasket = 'false';
    const label = addBtn.querySelector('span');
    if (label) label.textContent = row.outOfStock ? 'Out of stock' : 'Add';
    else addBtn.textContent = row.outOfStock ? 'Out of stock' : 'Add';
    reflectUpsellAttempt(addBtn, upsellAttempts.get(String(row.variantId)));
  }
  el.classList.toggle('rogue-upsell-row--soldout', Boolean(row.outOfStock));
}

/**
 * Reads a container's own pre-rendered `.rogue-upsell-row` elements
 * (snippets/rogue-upsell-row.liquid) back into the `candidates` shape
 * {@link upsellRows} takes - the row markup's data-* attributes are the one
 * source of truth for each candidate's handle/availability/tags/price/copy,
 * so a candidate is never described twice (once in Liquid, again in a
 * hand-kept JSON blob).
 * @param {Element|null} container
 * @returns {Record<string, UpsellCandidate>}
 */
function readCandidatesFromRows(container) {
  /** @type {Record<string, UpsellCandidate>} */
  const candidates = {};
  if (!container) return candidates;
  for (const el of container.querySelectorAll('[data-rogue-upsell-row][data-kind]')) {
    const kind = el.getAttribute('data-kind');
    if (!kind || kind === 'link') continue;
    const price = Number(el.getAttribute('data-price'));
    const addBtn = el.querySelector('[data-rogue-upsell-add]');
    candidates[kind] = {
      handle: el.getAttribute('data-handle') || '',
      available: el.getAttribute('data-available') === 'true',
      tags: (el.getAttribute('data-tags') || '')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
      price: Number.isFinite(price) ? price : null,
      needsChoice: el.getAttribute('data-needs-choice') === 'true',
      priceVaries: el.getAttribute('data-price-varies') === 'true',
      variantId: addBtn ? addBtn.getAttribute('data-variant-id') : null,
      title: el.getAttribute('data-title') || '',
      reason: el.getAttribute('data-reason') || '',
      tyreFittedNote: el.getAttribute('data-tyre-fitted-note') || '',
    };
  }
  return candidates;
}

/**
 * Wires every `[data-rogue-upsell-add]` button inside `root` to add its row
 * to the basket, then hands the fresh `/cart.js` response and the button's
 * own row element to `onAdded`.
 * @param {Element|null} root
 * @param {(context: { cart: any, row: Element, variantId: string, quantity: number }) => void} onAdded
 * @param {'product'|'cart'} eventContext
 */
const UPSELL_ADD_BOUND = new WeakSet();
const OPEN_FITMENT_BOUND = new WeakSet();

function bindUpsellAdds(root, onAdded, eventContext, computeRows) {
  if (!root || UPSELL_ADD_BOUND.has(root)) return;
  UPSELL_ADD_BOUND.add(root);
  root.addEventListener('click', async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = /** @type {HTMLButtonElement|null} */ (target.closest('[data-rogue-upsell-add]'));
    if (!button || button.disabled || button.hidden) return;
    if (button.dataset.checkBasket === 'true') {
      window.location.href = '/cart';
      return;
    }
    const row = button.closest('[data-rogue-upsell-row]');
    const variantId = button.dataset.variantId;
    const quantity = Number(button.dataset.quantity) || 1;
    if (!variantId) return;

    const previous = upsellAttempts.get(variantId);
    if (previous) {
      reflectUpsellAttempt(button, previous);
      if (previous.state !== 'pending') window.location.href = '/cart';
      return;
    }

    const kind = row?.getAttribute('data-kind');
    upsellAttempts.set(variantId, { state: 'pending', kind });
    button.disabled = true;
    const finishIntent = beginCartMutation();
    try {
    if (kind === 'tpms') {
      try {
        const cart = await fetchCart();
        if (!computeRows) throw new Error('Sensor quantities unavailable');
        const fresh = computeRows(cart.items).rows.find((value) => value.kind === 'tpms');
        // A changed basket needs a new explicit click on the revised quantity
        // and total. Never silently increase an accessory order from stale UI.
        if (!fresh || fresh.quantity !== quantity || String(fresh.variantId) !== variantId || fresh.outOfStock) {
          upsellAttempts.delete(variantId);
          if (fresh) {
            fillUpsellRow(row, fresh);
            const reason = row.querySelector('.rogue-upsell-row__reason');
            if (reason) reason.textContent = `Basket changed. Check the updated quantity and total before adding. ${fresh.reason}`;
          } else if (row) row.hidden = true;
          await onAdded({ cart, row, variantId, quantity: 0 });
          return;
        }
      } catch {
        upsellAttempts.delete(variantId);
        button.disabled = false;
        button.textContent = 'Retry basket check';
        const reason = row?.querySelector('.rogue-upsell-row__reason');
        if (reason) reason.textContent = 'We could not check your basket. No sensors were added. Try again to check the quantity first.';
        return;
      }
    }
    let refreshedCart;
    const result = await addBasketItemsOnce({ items: [{ id: Number(variantId), quantity }] }, { afterAccepted: async () => {
      const cart = await fetchCart();
      refreshedCart = cart;
      await dispatchUpsellAdd({ variantId, quantity, cartJson: cart, context: eventContext });
      if (kind !== 'tpms') await onAdded({ cart, row, variantId, quantity });
    } });
    upsellAttempts.set(variantId, { ...result, kind });
    for (const candidate of document.querySelectorAll('[data-rogue-upsell-add]')) {
      if (candidate.dataset.variantId === variantId) reflectUpsellAttempt(candidate, result);
    }
    if (kind === 'tpms' && result.state === 'accepted' && result.refreshed) {
      try { await onAdded({ cart: refreshedCart, row, variantId, quantity }); } catch { /* accepted; a later refresh can recalculate */ }
    }
    } finally {
      finishIntent();
    }
  });
}

/* ===========================================================================
 * Custom elements
 * ======================================================================= */

if (typeof HTMLElement !== 'undefined') {
  /**
   * sections/rogue-complete-set.liquid: the wheel product page's "Complete
   * your set" in-page block and post-add panel (design spec 4.3).
   */
  class RogueCompleteSet extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-rogue-cs-config]');
      try {
        this.config = configEl ? JSON.parse(configEl.textContent || '{}') : {};
      } catch {
        this.config = {};
      }
      this.tpmsVans = parseSimpleList(this.config.tpmsVansRaw || '');
      this.coverBodyTypes = parseSimpleList(this.config.coverBodyTypesRaw || '');
      this.excludeTags = parseSimpleList(this.config.excludeTagsRaw || '');

      this.inpage = this.querySelector('[data-rogue-cs-inpage]');
      this.inpageRows = this.querySelector('[data-rogue-cs-inpage-rows]');
      this.tpmsLink = this.inpage?.querySelector('[data-rogue-upsell-row][data-kind="link"]') || null;
      this.panel = this.querySelector('[data-rogue-cs-panel]');
      this.panelHeading = this.querySelector('[data-rogue-cs-panel-heading]');
      this.panelWheelLine = this.querySelector('[data-rogue-cs-panel-wheel-line]');
      this.panelFitCheck = this.querySelector('[data-rogue-cs-panel-fit]');
      this.panelRows = this.querySelector('[data-rogue-cs-panel-rows]');
      this.panelTotal = this.querySelector('[data-rogue-cs-panel-total]');
      this.panelCheckout = this.querySelector('[data-rogue-cs-panel-checkout]');
      this.panelActions = this.querySelector('[data-rogue-cs-panel-actions]');
      this.panelGoto = this.querySelector('[data-rogue-cs-panel-goto]');
      this.panelMerch = this.querySelector('[data-rogue-cs-panel-merch]');
      this.panelSubheading = this.querySelector('[data-rogue-cs-panel-subheading]');
      this.panelClose = this.querySelector('[data-rogue-cs-panel-close]');
      this.panelNoThanks = this.querySelector('[data-rogue-cs-panel-no-thanks]');

      this.previewMode = readPreviewParam();
      this.returnFocusEl = null;

      bindUpsellAdds(this.inpageRows, ({ cart }) => this.updateTotalsFromCart(cart), 'product', (items) => this.computeRows(items));
      bindUpsellAdds(this.panelRows, ({ cart }) => this.updateTotalsFromCart(cart), 'product', (items) => this.computeRows(items));

      this.panelClose?.addEventListener('click', () => this.closePanel());
      this.panelNoThanks?.addEventListener('click', () => {
        this.dismissForSession();
        this.closePanel();
      });
      this.panel?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closePanel();
      });

      this.mountBuyBoxExtras();
      this.refreshInPage();
      this.onPackageChange = () => this.refreshInPage();
      document.addEventListener('rogue:package-change', this.onPackageChange);
      this.bindLowStock();
      this.bindCartAdd();
    }

    dismissKey() {
      return `rogue.upsell.dismissed.${this.config.productId ?? ''}`;
    }

    isDismissed() {
      try {
        return window.sessionStorage.getItem(this.dismissKey()) === '1';
      } catch {
        return false;
      }
    }

    dismissForSession() {
      try {
        window.sessionStorage.setItem(this.dismissKey(), '1');
      } catch {
        /* private mode / quota - the panel simply stays available for the rest of the session */
      }
    }

    /**
     * @param {Array<Record<string, unknown>>} cartItems
     * @param {{ withPackage?: boolean }} [options] - withPackage: as the basket would be once the buy box's package is
     *   in it too (the buy box's extras), rather than the package standing in only for an empty basket
     */
    computeRows(cartItems, { withPackage = false } = {}) {
      const vehicle = readStoredVehicle();
      const vanSlug = (vehicle && vehicle.slug) || '';
      const vanBodyType = readVehicleBodyType(vanSlug);
      const gate = document.querySelector('rogue-fitment-gate');
      const previewPackage = gate ? { product_type: 'Wheel', quantity: gate.currentQuantity?.() || 0,
        properties: { _vehicle_slug: gate.vehicle?.slug || '', _spare_quantity: gate.spareIncluded ? '1' : '0' } } : null;
      const items = Array.isArray(cartItems) ? cartItems : [];
      return upsellRows({
        cartItems: withPackage && previewPackage ? [...items, previewPackage] : items,
        vanSlug,
        vanBodyType,
        tpmsVans: this.tpmsVans,
        candidates: readCandidatesFromRows(this.inpageRows),
        excludeTags: this.excludeTags,
        showCover: Boolean(this.config.showCover),
        coverBodyTypes: this.coverBodyTypes,
        previewMode: withPackage ? false : this.previewMode,
        previewPackage: withPackage ? null : previewPackage,
      });
    }

    /* ------------------------- Extras in the buy box (31 Oct 2026) ------------------------- */

    /**
     * Skye, 31 Oct 2026: add-ons during the process, not after it. With the
     * buy box's empty slot on the page (snippets/rogue-fitment-gate.liquid,
     * ref="extrasSlot"), this block's rows become tickboxes there, just
     * above Add to basket, and the old in-page block below the buy box stays
     * hidden. Same candidates, settings and rules (upsellRows), worked out
     * for the basket as it would be after this add. None start ticked; the
     * buy box adds the ticked ones with the wheels (RogueFitmentGate
     * setExtrasProvider). The post-add sheet is unchanged.
     * @returns {boolean} whether the buy box took the extras
     */
    mountBuyBoxExtras() {
      const gate = document.querySelector('rogue-fitment-gate');
      const slot = gate?.querySelector('[ref="extrasSlot"]');
      if (!slot || typeof gate.setExtrasProvider !== 'function') return false;
      this.extrasGate = gate;
      this.extrasSlot = slot;
      this.extrasCandidates = readCandidatesFromRows(this.inpageRows);
      this.extrasTicked = new Set();
      this.extrasShown = [];
      // The "Why?" toggles a shopper has opened, kept across redraws.
      this.extrasOpenReasons = new Set();

      const fieldset = document.createElement('fieldset');
      fieldset.className = 'rogue-buy__extras-set';
      const legend = document.createElement('legend');
      legend.className = 'rogue-label';
      legend.textContent = 'Add to your order';
      this.extrasList = document.createElement('div');
      this.extrasList.className = 'rogue-buy__extras-list';
      this.extrasNote = document.createElement('p');
      this.extrasNote.className = 'rogue-buy__option-note';
      this.extrasNote.hidden = true;
      fieldset.append(legend, this.extrasList, this.extrasNote);
      // The "does your van show tyre pressures?" link, for a van not on the sensor list.
      if (this.tpmsLink) fieldset.append(this.tpmsLink);
      slot.replaceChildren(fieldset);

      fieldset.addEventListener('change', (event) => {
        const box = /** @type {HTMLInputElement} */ (event.target);
        const kind = box?.dataset?.kind;
        if (!kind) return;
        if (box.checked) this.extrasTicked.add(kind);
        else this.extrasTicked.delete(kind);
        this.extrasGate.extrasChanged?.();
      });
      fieldset.addEventListener('click', (event) => {
        const why = /** @type {HTMLElement} */ (event.target)?.closest?.('.rogue-buy__extra-why');
        if (!why) return;
        const open = toggleExtraReason(why);
        const kind = why.dataset.kind || '';
        if (open) this.extrasOpenReasons.add(kind);
        else this.extrasOpenReasons.delete(kind);
      });

      gate.setExtrasProvider({
        totalCents: () => extrasSelection(this.extrasShown, this.extrasTicked).cents,
        summary: () => extrasSelection(this.extrasShown, this.extrasTicked).summary,
        itemsForAdd: (cartItems) => this.extrasForAdd(cartItems),
        // T2 (31 Oct 2026): the post-add sheet is the one confirmation when
        // it will open; the buy box then skips its own "Added" panel.
        confirmsAdd: () => Boolean(this.panel) && !this.isDismissed(),
        reset: () => {
          this.extrasTicked.clear();
          for (const box of this.extrasList.querySelectorAll('input[type="checkbox"]')) box.checked = false;
          this.extrasGate.extrasChanged?.();
        },
      });
      return true;
    }

    /** @param {UpsellRow} row @returns {string} the row's second line in the buy box */
    extrasReason(row, cartItems) {
      if (row.kind !== 'tpms') return row.reason;
      const candidate = this.extrasCandidates.tpms;
      const existing = tpmsRequirement(cartItems, this.tpmsVans, candidate).existing;
      const tyres = this.extrasGate?.tyresEnabled && this.extrasGate?.resolvedTyreLine?.() ? candidate?.tyreFittedNote || '' : '';
      const already = existing > 0 ? `${existing} already in your basket.` : '';
      return [candidate?.reason || '', already, tyres].filter(Boolean).join(' ');
    }

    /**
     * Shows the rows that apply now, keeping each tick that still applies.
     * @param {{ rows: UpsellRow[], tpmsNote?: string, showTpmsLink?: boolean }} computed - computeRows(..., { withPackage: true })
     * @param {Array<Record<string, unknown>>} cartItems
     */
    renderBuyBoxExtras(computed, cartItems) {
      const rows = buyBoxExtraRows(computed.rows, this.extrasCandidates);
      const kinds = new Set(rows.map((row) => row.kind));
      for (const kind of [...this.extrasTicked]) if (!kinds.has(kind)) this.extrasTicked.delete(kind);
      this.extrasShown = rows;
      // B5 (31 Oct 2026): one compact line per extra; the reason sits behind
      // a "Why?" toggle so three extras take about 180px, not 490px.
      this.extrasList.replaceChildren(...rows.map((row) => buildBuyBoxExtraRow(row, this.extrasReason(row, cartItems), this.extrasTicked.has(row.kind), this.extrasOpenReasons)));
      const note = computed.tpmsNote || '';
      this.extrasNote.textContent = note;
      this.extrasNote.hidden = !note;
      if (this.tpmsLink) this.tpmsLink.hidden = !computed.showTpmsLink;
      this.extrasSlot.hidden = rows.length === 0 && !note && !computed.showTpmsLink;
      this.extrasGate.extrasChanged?.();
    }

    /**
     * The ticked extras for the buy box's add, worked out again from the
     * basket as it is at the moment of adding. If that changes any ticked
     * row (the sensors already in the basket, say), nothing is added: the
     * rows are redrawn and the shopper checks them again, never a quantity
     * or total they did not see.
     * @param {Array<Record<string, unknown>>} cartItems
     * @returns {{ items: Array<{ id: string|number, quantity: number }>, error: string }}
     */
    extrasForAdd(cartItems) {
      if (this.extrasTicked.size === 0) return { items: [], error: '' };
      const computed = this.computeRows(cartItems, { withPackage: true });
      const fresh = buyBoxExtraRows(computed.rows, this.extrasCandidates);
      const changed = [...this.extrasTicked].some((kind) => {
        const before = this.extrasShown.find((row) => row.kind === kind);
        const now = fresh.find((row) => row.kind === kind);
        return !before || !now || now.quantity !== before.quantity || String(now.variantId) !== String(before.variantId) || now.unitPrice !== before.unitPrice;
      });
      if (changed) {
        this.renderBuyBoxExtras(computed, cartItems);
        return { items: [], error: 'Your basket has changed, so we have updated the extras. Check them, then add to basket again.' };
      }
      return { items: extrasSelection(fresh, this.extrasTicked).items, error: '' };
    }

    async refreshInPage() {
      if (!this.inpage) return;
      const version = this.refreshVersion = (this.refreshVersion || 0) + 1;
      let cart;
      try {
        cart = await fetchCart();
      } catch {
        if (version !== this.refreshVersion) return;
        if (this.extrasSlot) {
          // No basket to check against: offer no extras rather than a wrong sensor count.
          this.renderBuyBoxExtras({ rows: [], tpmsNote: '', showTpmsLink: false }, []);
          return;
        }
        renderUpsellRows(this.inpageRows, [], 4);
        if (this.tpmsLink) this.tpmsLink.hidden = true;
        const note = this.querySelector('[data-rogue-tpms-note]');
        if (note) { note.textContent = 'We could not check your basket. Refresh before choosing extras.'; note.hidden = false; }
        this.inpage.hidden = false;
        return;
      }
      if (version !== this.refreshVersion) return;
      if (this.extrasSlot) {
        this.inpage.hidden = true;
        this.renderBuyBoxExtras(this.computeRows(cart.items || [], { withPackage: true }), cart.items || []);
        return;
      }
      const computed = this.computeRows(cart.items || []);
      renderUpsellRows(this.inpageRows, computed.rows, 4);
      if (this.tpmsLink) this.tpmsLink.hidden = !computed.showTpmsLink;
      const note = this.querySelector('[data-rogue-tpms-note]');
      if (note) { note.textContent = computed.tpmsNote; note.hidden = !computed.tpmsNote; }
      this.inpage.hidden = computed.rows.length === 0 && !computed.showTpmsLink && !computed.tpmsNote;
    }

    disconnectedCallback() {
      document.removeEventListener('rogue:package-change', this.onPackageChange);
    }

    updateTotalsFromCart(cart) {
      if (this.panelTotal && !this.panel?.hidden) this.panelTotal.textContent = this.buildTotalLine(cart);
      if (this.panelCheckout && !this.panel?.hidden) this.panelCheckout.hidden = !basketReadyForCheckout(cart.items);
      this.refreshInPage();
    }

    buildTotalLine(cart) {
      const template =
        (this.config.copy && this.config.copy.basketTotalTemplate) ||
        'Basket total: [total]. Prices include VAT. Free UK delivery with code FREESHIP.';
      const line = template.split('[total]').join(formatPence(cart.total_price));
      // 1 Nov 2026: no FREESHIP while a Whack Friday drop is live (snippets/rogue-freeship-state.liquid).
      return document.documentElement.getAttribute('data-rogue-freeship') === 'off' ? withoutCodeSentences(line, 'FREESHIP') : line;
    }

    buildWheelLine(cart) {
      const wheelItem = (cart.items || []).find((item) => item && item.product_type === 'Wheel');
      if (!wheelItem) return '';
      const variantTitle = wheelItem.variant_title || '';
      const titlePart = variantTitle ? `${wheelItem.product_title}, ${variantTitle}` : wheelItem.product_title;
      return `${wheelItem.quantity} x ${titlePart}: ${formatPence(wheelItem.line_price)}`;
    }

    bindLowStock() {
      if (!this.config.showLowStock) return;
      const map = this.config.lowStockMap || {};
      document.addEventListener('rogue:variant-change', (event) => {
        this.updateLowStockNote(map, event.detail && event.detail.variantId);
      });
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('variant');
      if (fromUrl) this.updateLowStockNote(map, Number(fromUrl));
      else if (this.config.currentVariantId != null) this.updateLowStockNote(map, this.config.currentVariantId);
    }

    updateLowStockNote(map, variantId) {
      const anchor = document.querySelector('[ref="normalAddArea"]');
      if (!anchor) return;
      const existing = anchor.previousElementSibling;
      const isNote = Boolean(existing && existing.classList && existing.classList.contains('rogue-stock-note'));
      const qty = variantId != null ? map[String(variantId)] : undefined;
      const text = lowStockText(qty);
      if (text === '') {
        if (isNote) existing.remove();
        return;
      }
      if (isNote) {
        existing.textContent = text;
      } else {
        const note = document.createElement('p');
        note.className = 'rogue-stock-note';
        note.textContent = text;
        anchor.insertAdjacentElement('beforebegin', note);
      }
    }

    bindCartAdd() {
      import('@shopify/events')
        .then(({ StandardEvents }) => {
          document.addEventListener(StandardEvents.cartLinesUpdate, (event) => this.onCartLinesUpdate(event));
        })
        .catch((error) => console.warn('[rogue-complete-set] cart listener not wired up:', error));
    }

    async onCartLinesUpdate(event) {
      if (event.action !== 'add') return;
      if (event.detail && event.detail.source === 'rogue-upsell') return;
      const lines = Array.isArray(event.lines) ? event.lines : [];
      const variantIds = (this.config.variantIds || []).map(String);
      const matches = lines.some((line) => variantIds.includes(extractNumericId(line.merchandiseId)));
      if (!matches) return;

      let result;
      try {
        result = await event.promise;
      } catch {
        /* the add itself failed elsewhere - nothing to open a panel about */
      }
      // The package id the buy box just used, so the sheet prices that set
      // (wheels plus fitted tyres), not an older set of the same wheel.
      this.lastSetId = result?.detail?.setId || event.detail?.setId || '';
      // The extras added in the same click (T2), so the summary prices them too.
      const extraIds = result?.detail?.extraVariantIds || event.detail?.extraVariantIds;
      this.lastExtraVariantIds = Array.isArray(extraIds) ? extraIds.map(String) : [];

      if (this.isDismissed()) return;

      let cart;
      try {
        cart = await fetchCart();
      } catch {
        return;
      }
      this.openPanel(cart);
    }

    openPanel(cart) {
      if (!this.panel) return;
      const computed = this.computeRows(cart.items || []);
      // The sheet is the add confirmation too (25 Oct 2026): it opens with
      // the set's real total and "Check out" even when there is nothing
      // worth suggesting, and simply drops the extras block then.
      // T2 (31 Oct 2026): with the extras offered in the buy box, the sheet
      // does not offer them again; it confirms the add and its real total.
      const hasRows = computed.rows.length > 0 && !this.extrasSlot;
      renderUpsellRows(this.panelRows, hasRows ? computed.rows : [], 3);
      if (this.panelRows) this.panelRows.hidden = !hasRows;
      if (this.panelSubheading) this.panelSubheading.hidden = !hasRows;
      const summary = addedSetSummary(cart.items, { variantIds: this.config.variantIds, setId: this.lastSetId, extraVariantIds: this.lastExtraVariantIds });
      if (this.panelWheelLine) this.panelWheelLine.textContent = summary ? summary.text : this.buildWheelLine(cart);
      if (this.panelFitCheck) this.panelFitCheck.hidden = !addedSetNeedsFitCheck(cart.items, { setId: this.lastSetId });
      if (this.panelCheckout) this.panelCheckout.hidden = !basketReadyForCheckout(cart.items);
      if (this.panelTotal) this.panelTotal.textContent = this.buildTotalLine(cart);
      applyDropSheet(
        { actions: this.panelActions, checkout: this.panelCheckout, goto: this.panelGoto, merch: this.panelMerch },
        document.documentElement.getAttribute('data-rogue-drop') === 'live'
      );

      this.returnFocusEl = document.querySelector('[ref="addToCartConfirmation"]');

      this.panel.hidden = false;
      this.panel.classList.toggle('rogue-cs-panel--no-motion', prefersReducedMotion());
      requestAnimationFrame(() => this.panel.classList.add('is-open'));

      if (this.panelHeading) {
        if (!this.panelHeading.hasAttribute('tabindex')) this.panelHeading.setAttribute('tabindex', '-1');
        this.panelHeading.focus();
      }

      this.refreshInPage();
    }

    closePanel() {
      if (!this.panel || this.panel.hidden) return;
      this.panel.hidden = true;
      this.panel.classList.remove('is-open');
      const focusTarget = this.returnFocusEl;
      if (focusTarget && document.contains(focusTarget)) {
        if (!focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus();
      }
    }
  }

  /**
   * sections/rogue-basket-suggestions.liquid: the basket's "Finish your
   * set" / "You might also need" / "More from the workshop" rows (design
   * spec 4.5). The section's own Liquid already picked the mode (basket
   * composition is server data); this only decides the van-dependent rows
   * (TPMS, cover) and wires the Add buttons.
   */
  class RogueBasketSuggestions extends HTMLElement {
    connectedCallback() {
      this.refreshVersion = 0;
      this.init();
      this.onCartChange = async (event) => {
        // Hide stale actions immediately, including while the mutation is pending.
        this.refreshVersion++;
        this.hidden = true;
        try {
          const result = await event.promise;
          if (result?.detail?.didError || !this.isConnected) return;
          await this.rerenderSection();
        } catch { /* Keep stale suggestions hidden after an unconfirmed change. */ }
      };
      import('@shopify/events').then(({ StandardEvents }) => {
        if (!this.isConnected) return;
        this.cartEventName = StandardEvents.cartLinesUpdate;
        document.addEventListener(this.cartEventName, this.onCartChange);
      }).catch(() => {});
    }

    disconnectedCallback() {
      this.refreshVersion++;
      if (this.cartEventName) document.removeEventListener(this.cartEventName, this.onCartChange);
    }

    init() {
      const configEl = this.querySelector('[data-rogue-bs-config]');
      try {
        this.config = configEl ? JSON.parse(configEl.textContent || '{}') : {};
      } catch {
        this.config = {};
      }
      this.tpmsVans = parseSimpleList(this.config.tpmsVansRaw || '');
      this.coverBodyTypes = parseSimpleList(this.config.coverBodyTypesRaw || '');
      this.excludeTags = parseSimpleList(this.config.excludeTagsRaw || '');
      this.rowsContainer = this.querySelector('[data-rogue-bs-rows]');
      bindUpsellAdds(this.rowsContainer, () => this.rerenderSection(), 'cart', (items) => this.computeWheelRows(items));
      this.bindOpenFitment();
      if (this.config.mode === 'wheel') this.refreshWheelRows();
      else if (this.config.mode === 'accessories') this.refreshSimpleRows(['rinse', 'brace', 'sticker'], 3);
      else if (this.config.mode === 'merch') this.refreshSimpleRows(['merch1', 'merch2'], 2);
    }

    /**
     * Review finding B3 (26 Sep 2026): "Finish your set" rendered its
     * heading with nothing under it whenever no candidate applied - every
     * wheels-and-tyres basket today, since TPMS/brace/Rinse/cover are
     * already excluded or already in the basket (133px of dead space at
     * 1440). Design spec rule 4.1.6: "a step with no candidates renders
     * nothing" - so the whole section hides unless there's a visible row,
     * or one of the three static notes/links the server already decided to
     * print (the tyre link, the registration prompt, the excluded-item
     * note).
     * @param {boolean} hasRows
     */
    updateVisibility(hasRows) {
      const hasStaticContent = Boolean(
        this.querySelector('[data-rogue-bs-reg-note], [data-rogue-bs-excluded-note]')
      );
      const tpmsNote = this.querySelector('[data-rogue-tpms-note]');
      this.hidden = !hasRows && !hasStaticContent && !(tpmsNote && !tpmsNote.hidden && tpmsNote.textContent.trim());
    }

    /**
     * Review finding S9 (26 Sep 2026): the registration prompt's link was
     * `href="#"` with no handler, so a click just jumped to the top of the
     * page - and it said "below", when the fitment panel (sections/main-
     * cart.liquid's "Help us double-check fitment") sits above it. Wired to
     * scroll to and focus the registration input instead (copy fixed in the
     * section's own schema default).
     */
    bindOpenFitment() {
      const link = this.querySelector('[data-rogue-bs-open-fitment]');
      if (!link || OPEN_FITMENT_BOUND.has(link)) return;
      OPEN_FITMENT_BOUND.add(link);
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const input = document.querySelector('[ref="registrationInput"]');
        if (!input) return;
        const details = input.closest('details');
        if (details && !details.open) details.open = true;
        input.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
        input.focus();
      });
    }

    /**
     * "You might also need" / "More from the workshop" (design spec 4.5):
     * neither depends on the stored van, only on availability and what's
     * already in the basket - all server data, but the row markup is still
     * revealed by script only, same as every other upsell row (design spec
     * section 4.4's opening rule).
     *
     * Review finding S5 (26 Sep 2026): the exclude-tag list is "tags a wheel
     * set already includes" (locking nuts, TPMS, spigot rings, centre
     * caps...) - it never applied here (there's no set to duplicate in an
     * accessories- or merch-only basket), but passing it anyway meant the
     * centre cap sticker (tagged `centre-caps` and `centre-caps-and-trim`)
     * could never appear in "You might also need". Wheel mode's own
     * {@link refreshWheelRows} still passes it to {@link upsellRows}, where
     * it's the correct, intended rule.
     * @param {string[]} order
     * @param {number} limit
     */
    async refreshSimpleRows(order, limit) {
      const version = ++this.refreshVersion;
      let cart;
      try {
        cart = await fetchCart();
      } catch {
        if (version !== this.refreshVersion || !this.isConnected) return;
        renderUpsellRows(this.rowsContainer, [], limit);
        this.updateVisibility(false);
        return;
      }
      if (version !== this.refreshVersion || !this.isConnected) return;
      const candidates = readCandidatesFromRows(this.rowsContainer);
      const rows = pickAvailableCandidates({
        order,
        candidates,
        cartItems: cart.items || [],
        limit,
      });
      renderUpsellRows(this.rowsContainer, rows, limit);
      this.updateVisibility(rows.length > 0);
    }

    async refreshWheelRows() {
      const version = ++this.refreshVersion;
      let cart;
      try {
        cart = await fetchCart();
      } catch {
        if (version !== this.refreshVersion || !this.isConnected) return;
        renderUpsellRows(this.rowsContainer, [], 4);
        const note = this.querySelector('[data-rogue-tpms-note]');
        if (note) { note.textContent = 'We could not check your basket. Refresh before choosing extras.'; note.hidden = false; }
        this.updateVisibility(true);
        return;
      }
      if (version !== this.refreshVersion || !this.isConnected) return;
      const computed = this.computeWheelRows(cart.items);
      renderUpsellRows(this.rowsContainer, computed.rows, 4);
      const note = this.querySelector('[data-rogue-tpms-note]');
      if (note) { note.textContent = computed.tpmsNote; note.hidden = !computed.tpmsNote; }
      this.updateVisibility(computed.rows.length > 0 || Boolean(computed.tpmsNote));
    }

    computeWheelRows(cartItems) {
      const vehicle = readStoredVehicle();
      const vanSlug = (vehicle && vehicle.slug) || '';
      const vanBodyType = readVehicleBodyType(vanSlug);
      return upsellRows({
        cartItems,
        vanSlug,
        vanBodyType,
        tpmsVans: this.tpmsVans,
        candidates: readCandidatesFromRows(this.rowsContainer),
        excludeTags: this.excludeTags,
        showCover: Boolean(this.config.showCover),
        coverBodyTypes: this.coverBodyTypes,
        previewMode: readPreviewParam(),
      });
    }

    async rerenderSection() {
      const sectionId = this.config.sectionId;
      if (!sectionId) return;
      this.refreshRequested = true;
      if (this.refreshRunning) return this.refreshRunning;
      this.refreshRunning = (async () => {
        try {
          const { sectionRenderer } = await import('@theme/section-renderer');
          do {
            this.refreshRequested = false;
            await sectionRenderer.renderSection(sectionId, { cache: false });
          } while (this.refreshRequested && this.isConnected);
          if (this.isConnected) this.init();
        } catch {
          this.hidden = true;
        }
      })().finally(() => { this.refreshRunning = null; });
      return this.refreshRunning;
    }
  }

  /**
   * sections/rogue-sale-help.liquid: the postcode delivery check, "Talk to
   * us before you buy" (with its call-back form), and, on the basket, "What
   * happens after you order" (design spec 5).
   */
  class RogueSaleHelp extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-rogue-sh-config]');
      try {
        this.config = configEl ? JSON.parse(configEl.textContent || '{}') : {};
      } catch {
        this.config = {};
      }

      this.form = this.querySelector('[data-rogue-sh-postcode-form]');
      this.input = this.querySelector('[data-rogue-sh-postcode-input]');
      this.result = this.querySelector('[data-rogue-sh-postcode-result]');
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.checkPostcode();
      });

      this.fillCallbackFields();
      this.bindDisclosureAutoOpen();
    }

    checkPostcode() {
      if (!this.input || !this.result) return;
      const parsed = parsePostcode(this.input.value);
      if (!parsed.valid) {
        this.renderResult(this.config.messages?.invalid || "That doesn't look like a UK postcode. Check it and try again, or call us on 0118 405 0166.");
        return;
      }

      try {
        window.localStorage.setItem('rogue.delivery_area', parsed.outward);
      } catch {
        /* private mode / quota - the check still works for this visit */
      }

      // Staff can change delivery rules in the backend. Saved theme zone and
      // dispatch copy cannot establish today's price or availability.
      const lines = [`Delivery to ${parsed.outward}: delivery calculated at checkout from your full address and basket. If no option is available, contact us for a quote.`];
      if (this.config.isPreorder) lines.push('For pre-orders, contact us to confirm dispatch timing.');
      // "Or collect from our Reading showroom. [Delivery details]" is a
      // permanent line under the postcode field (with a real link), not
      // part of this aria-live text - see the static paragraph below.
      this.renderResult(lines.filter(Boolean).join(' '));
    }

    renderResult(text) {
      if (this.result) this.result.textContent = text;
    }

    fillCallbackFields() {
      const vehicle = readStoredVehicle();
      const vanField = this.querySelector('[data-rogue-sh-van-field]');
      if (vanField) vanField.value = (vehicle && vehicle.display_name) || '';
    }

    bindDisclosureAutoOpen() {
      // The contact form's own result (`form.posted_successfully?`) is only
      // known inside Shopify's `{% form %}` tag, after the `<details>` that
      // wraps it has already opened - so the section can't set `open` on
      // that tag itself. If the thank-you line rendered, this opens its
      // `<details>` and moves focus there instead.
      const thanks = this.querySelector('[data-rogue-sh-thanks]');
      if (thanks) {
        thanks.closest('details')?.setAttribute('open', '');
        thanks.focus();
      }
    }
  }

  if (window.customElements) {
    if (!customElements.get('rogue-complete-set')) customElements.define('rogue-complete-set', RogueCompleteSet);
    if (!customElements.get('rogue-basket-suggestions'))
      customElements.define('rogue-basket-suggestions', RogueBasketSuggestions);
    if (!customElements.get('rogue-sale-help')) customElements.define('rogue-sale-help', RogueSaleHelp);
  }
}

/** @returns {boolean} whether the current URL carries `?rogue_upsell_preview=1` (design spec 4.4/4.3(d)) */
function readPreviewParam() {
  try {
    return new URLSearchParams(window.location.search).get('rogue_upsell_preview') === '1';
  } catch {
    return false;
  }
}
